import React, { FC } from 'react';
import { Box, Divider, MainContentBox, Typography } from 'enova-frontend-components';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import RouterLink from '../../components/routerLink';
import {
  AllRegistrationScreenParams,
  BuildingScreenTab,
  camelCasify,
  getBuildingScreenPath,
  RegistrationCommonStep
} from '../../utils/navigation';
import BuildingHeaderInfo from '../../components/buildingHeaderInfo';
import BuildingDetailsProvider from '../../hooks/useBuildingDetails/provider';
import { useBuildingData } from '../../hooks/useBuildingDetails/buildingDetailsHooks';
import { LoadingIndicator } from '../../components/loadingIndicator';
import { useGetBuildingAddress } from '../../hooks/useGetBuildingAddress';

export const RegistrationLayout: FC = () => {
  const { t } = useTranslation();
  const { buildingId, step } = useParams<AllRegistrationScreenParams>();
  const { getBuildingAddress } = useGetBuildingAddress();
  const { buildingLoading, building } = useBuildingData(buildingId);

  if (!buildingId) {
    return null;
  }

  if (buildingLoading) {
    return <LoadingIndicator />;
  }

  const buildingPath = getBuildingScreenPath({
    buildingId,
    tab: BuildingScreenTab.ENERGY_RATING
  });

  const currentStep = step || RegistrationCommonStep.ABOUT;
  const stepTitle = t(`register.step.${camelCasify(currentStep)}`);

  const helmetTitle = [t('registerScreen.pageTitle'), stepTitle, getBuildingAddress(building?.adresse)].join(' | ');

  return (
    <BuildingDetailsProvider>
      <Helmet title={helmetTitle} />

      <MainContentBox>
        <RouterLink className="mb-3 as-start" to={buildingPath}>
          {t('navigation.registerScreen.back')}
        </RouterLink>

        <Box display="flex" alignItems="flex-start" flexDirection="column">
          <Typography variant="h1" mb={0}>
            {t('registerScreen.pageTitle')}
          </Typography>

          <BuildingHeaderInfo energiobjektId={buildingId} AddressProps={{ variant: 'h2', component: 'p' }} />
        </Box>
      </MainContentBox>

      <Divider />

      <MainContentBox>
        <Outlet />
      </MainContentBox>
    </BuildingDetailsProvider>
  );
};
